import { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Button, Chip, useTheme, Stack, IconButton, Tooltip, CircularProgress, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { DataGrid } from '@mui/x-data-grid';

import KPICard from './KPICard';
import SubCard from '../SubCard';
import MainCard from '../MainCard';
import { sendPVReminderEmail } from 'utils/API/Distributor APIs/DistributorAPiCalls';
import { getPVData } from 'utils/API/Distributor APIs/DistributorAPiCalls';
import { BCPhysicalVerificationSD } from 'utils/API/Superdistributor APIs/SuperdistributorApiCalls';
import { BCPhysicalVerificationRM, BCPhysicalVerificationSendEmailRM } from 'utils/API/RelationManagerAPIs/RelationManagerAPiCalls';
import PVDetailsModal from 'views/Distributor/PVDetailsModal';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HowToRegTwoToneIcon from '@mui/icons-material/HowToRegTwoTone';
import VideocamIcon from '@mui/icons-material/Videocam';
import AddTaskTwoToneIcon from '@mui/icons-material/AddTaskTwoTone';
import VerifiedTwoToneIcon from '@mui/icons-material/VerifiedTwoTone';
import VerifiedUserTwoToneIcon from '@mui/icons-material/VerifiedUserTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import Spinner from 'views/Components/LoadingSinner';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { BCPhysicalVerificationSendEmail } from 'utils/API/Superdistributor APIs/SuperdistributorApiCalls';

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const PhysicalVerificationMonitor = () => {
    const theme = useTheme();

    // Internal State - Data Management
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({});
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [totalCount, setTotalCount] = useState(0);
    const [activeSlaFilter, setActiveSlaFilter] = useState(null);
    const [activeTile, setActiveTile] = useState(null);

    // Modal State
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    // Email Action State
    const [sendingEmailId, setSendingEmailId] = useState(null);
    const [successEmailId, setSuccessEmailId] = useState(null);

    // Calculation for Ratios
    const vkycRatio = stats?.vkyc_complete ? stats?.vkyc_complete?.ratio + '%' : '0%';
    const allocatedRatio = stats?.pv_allocated ? stats?.pv_allocated?.ratio + '%' : '0%';
    const verifiedRatio = stats?.pv_completed ? stats?.pv_completed?.ratio + '%' : '0%';

    // Ensure displayed rows never exceed page size (Client-side safety slice for Server-side mode)
    // This fixes the issue where DataGrid shows "1-84 of 84" if API returns all rows.
    const displayedRows = Array.isArray(rows) ? rows.slice(0, Number(pageSize)) : [];

    // Data Fetching with Role-Based API Selection
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const userRole = localStorage.getItem('role');
            let apiCall;

            switch (userRole) {
                case 'distributor':
                    apiCall = getPVData;
                    break;
                case 'superdistributor':
                    apiCall = BCPhysicalVerificationSD;
                    break;
                case 'national_head':
                case 'zone_head':
                case 'state_head':
                case 'middle_manager':
                case 'manager':
                case 'SANCHALAK':
                    apiCall = BCPhysicalVerificationRM;
                    break;
                default:
                    console.error('Unknown role for PV data fetching:', userRole);
                    setLoading(false);
                    return;
            }

            const payload = {
                pageNumber: page + 1,
                length: pageSize,
                filter: activeSlaFilter,
                sort: activeTile || ''
            };

            const response = await apiCall(payload);
            if (response?.data) {
                setStats(response?.data?.tiles || {});
                // Slice data to ensure we don't show more than pageSize rows (fix for API returning all rows)
                const apiRows = response?.data?.data || [];
                // Force slice with validated number to prevent "Show All" bug
                setRows(apiRows.slice(0, Number(pageSize)));
                setTotalCount(response?.data?.count || 0);
            }
        } catch (error) {
            console.error('Failed to fetch PV data', error);
        }
        setLoading(false);
    }, [page, pageSize, activeSlaFilter, activeTile]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Filter Handlers
    const handleFilterChange = (filter) => {
        setActiveSlaFilter(filter);
        setPage(0);
    };

    const handleTileChange = (tile) => {
        setActiveTile(tile);
        setPage(0);
    };

    // Handle SLA Filter Toggle
    const handleSlaFilter = (filterType) => {
        const newFilter = activeSlaFilter === filterType ? null : filterType;
        handleFilterChange(newFilter);
    };

    const handleView = (row) => {
        setSelectedRow(row);
        setViewModalOpen(true);
    };

    const handleEmail = async (row) => {
        if (!row?.id) return;

        setSendingEmailId(row.id);

        // Determine which API to call based on user role
        const userRole = localStorage.getItem('role');
        let apiCall;

        switch (userRole) {
            case 'distributor':
                apiCall = sendPVReminderEmail;
                break;
            case 'superdistributor':
                apiCall = BCPhysicalVerificationSendEmail;
                break;
            case 'national_head':
            case 'zone_head':
            case 'state_head':
            case 'manager':
            case 'middle_manager':
            case 'SANCHALAK':
                apiCall = BCPhysicalVerificationSendEmailRM;
                break;

            default:
                console.error('Unknown role:', userRole);
                setSendingEmailId(null);
                return;
        }

        const res = await apiCall({ agent_id: row.id });

        if (res?.status === 200) {
            globalNotifySuccess('Reminder email sent successfully');
            setSendingEmailId(null);
            setSuccessEmailId(row.id);
            // Reset success state after 3 seconds to allow re-sending if needed
            setTimeout(() => {
                setSuccessEmailId((prev) => (prev === row.id ? null : prev));
            }, 3000);
        } else {
            // Handle error, maybe show error toast
            setSendingEmailId(null);
        }
    };

    // Grid Columns
    const columns = [
        { field: 'bcname', headerName: 'Agent Name', width: 200 },
        { field: 'agent_code', headerName: 'ATA Code', width: 150 },
        { field: 'place', headerName: 'Place', width: 150 },
        {
            field: 'sla_status',
            headerName: 'TAT Status',
            width: 170,
            renderCell: (params) => {
                let color = 'default';
                if (params.value === 'TAT Expired') color = 'error';
                else if (params.value === 'TAT About to Expire') color = 'warning';
                else if (params.value === 'Within TAT') color = 'success';

                return <Chip label={params.value} size="small" color={color} variant="filled" />;
            }
        },
        {
            field: 'vkyc_status',
            headerName: 'VKYC Status',
            width: 150,
            renderCell: (params) => (
                <Chip label={params.value} size="small" color={params.value === 'Verified' ? 'success' : 'error'} variant="outlined" />
            )
        },
        { field: 'distributor', headerName: 'RO Name', width: 180 },
        { field: 'pcc_allocated_employee', headerName: 'Allocated To', width: 180 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120, // Reduced width since we use icons
            sortable: false,
            renderCell: (params) => {
                const isSending = sendingEmailId === params.row.id;
                const isSuccess = successEmailId === params.row.id;

                return (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleView(params.row)}
                                sx={{
                                    bgcolor: theme.palette.primary.light + '40',
                                    '&:hover': { bgcolor: theme.palette.primary.light }
                                }}
                            >
                                <VisibilityTwoToneIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isSuccess ? 'Sent!' : 'Send Reminder'}>
                            <IconButton
                                color={isSuccess ? 'success' : 'secondary'}
                                size="small"
                                onClick={() => !isSending && !isSuccess && handleEmail(params.row)}
                                disabled={isSending}
                                sx={{
                                    bgcolor: isSuccess ? theme.palette.success.light + '40' : theme.palette.secondary.light + '40',
                                    '&:hover': {
                                        bgcolor: isSuccess ? theme.palette.success.light : theme.palette.secondary.light
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <AnimatePresence mode="wait">
                                    {isSending ? (
                                        <motion.div
                                            key="loading"
                                            initial={{ opacity: 0, scale: 0.5 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                        >
                                            <CircularProgress size={20} color="inherit" />
                                        </motion.div>
                                    ) : isSuccess ? (
                                        <motion.div
                                            key="success"
                                            initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
                                            animate={{ opacity: 1, scale: 1.2, rotate: 0 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                        >
                                            <CheckCircleTwoToneIcon fontSize="small" />
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="idle"
                                            initial={{ opacity: 0, scale: 0.5 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                        >
                                            <EmailTwoToneIcon fontSize="small" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            }
        }
    ];

    // Row Styling for TAT Expired
    const getRowClassName = (params) => {
        if (params.row.sla_status === 'TAT Expired') return 'tat-expired-row';
        if (params.row.sla_status === 'TAT About to Expire') return 'tat-warning-row';
        return '';
    };

    // Custom Header
    const CustomHeader = (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <VerifiedUserTwoToneIcon
                sx={{
                    fontSize: '2rem',
                    color: theme.palette.primary.main,
                    opacity: 0.8
                }}
            />
            <Box>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 700,
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '0.5px'
                    }}
                >
                    Physical Verification Monitor
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 500 }}>
                    Track agent verification status, TAT metrics, and pending allocations
                </Typography>
            </Box>
        </Stack>
    );

    return (
        <MainCard title={CustomHeader}>
            <Box sx={{ width: '100%' }}>
                {/* KPI Tiles */}
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title={stats?.onboarding?.label}
                                count={stats?.onboarding?.count}
                                isActive={activeTile === stats?.onboarding?.label}
                                onClick={() => handleTileChange(stats?.onboarding?.label)}
                                color="primary"
                                loading={loading}
                                icon={<HowToRegTwoToneIcon fontSize="inherit" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title={stats?.vkyc_complete?.label}
                                count={stats?.vkyc_complete?.count}
                                ratio={vkycRatio}
                                isActive={activeTile === stats?.vkyc_complete?.label}
                                onClick={() => handleTileChange(stats?.vkyc_complete?.label)}
                                color="primary"
                                loading={loading}
                                icon={<VideocamIcon fontSize="inherit" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title={stats?.pv_allocated?.label}
                                count={stats?.pv_allocated?.count}
                                ratio={allocatedRatio}
                                isActive={activeTile === stats?.pv_allocated?.label}
                                onClick={() => handleTileChange(stats?.pv_allocated?.label)}
                                color="primary"
                                loading={loading}
                                icon={<AddTaskTwoToneIcon fontSize="inherit" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title={stats?.pv_completed?.label}
                                count={stats?.pv_completed?.count}
                                ratio={verifiedRatio}
                                isActive={activeTile === stats?.pv_completed?.label}
                                onClick={() => handleTileChange(stats?.pv_completed?.label)}
                                color="primary"
                                loading={loading}
                                icon={<VerifiedTwoToneIcon fontSize="inherit" />}
                            />
                        </Grid>
                    </Grid>
                </motion.div>

                {/* Detailed Data List Section */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <SubCard
                        title="Detailed Verifications"
                        secondary={
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant={activeSlaFilter === 'TAT About to Expire' ? 'contained' : 'outlined'}
                                    color="warning"
                                    size="small"
                                    startIcon={<WarningAmberIcon />}
                                    onClick={() => handleSlaFilter('TAT About to Expire')}
                                    sx={{
                                        borderRadius: 2,
                                        ...(activeSlaFilter !== 'TAT About to Expire' && {
                                            color: theme.palette.warning.dark,
                                            borderColor: theme.palette.warning.dark,
                                            '&:hover': {
                                                borderColor: theme.palette.warning.dark,
                                                backgroundColor: theme.palette.warning.light + '20'
                                            }
                                        })
                                    }}
                                >
                                    About to Expire
                                </Button>
                                <Button
                                    variant={activeSlaFilter === 'TAT Expired' ? 'contained' : 'outlined'}
                                    color="error"
                                    size="small"
                                    startIcon={<WarningAmberIcon />}
                                    onClick={() => handleSlaFilter('TAT Expired')}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Expired
                                </Button>
                            </Stack>
                        }
                    >
                        <Box
                            sx={{
                                height: 800, // Increased height as requested
                                width: '100%',
                                '& .tat-expired-row': {
                                    border: `2px solid ${theme.palette.error.main} !important`,
                                    bgcolor: '#fff4f4',
                                    '&:hover': {
                                        bgcolor: '#ffe3e3'
                                    }
                                },
                                '& .tat-warning-row': {
                                    border: `2px solid ${theme.palette.warning.main} !important`,
                                    bgcolor: '#fffbf2',
                                    '&:hover': {
                                        bgcolor: '#fff8e1'
                                    }
                                }
                            }}
                        >
                            <DataGrid
                                rows={displayedRows}
                                columns={columns}
                                loading={loading}
                                getRowClassName={getRowClassName}
                                checkboxSelection={false}
                                disableSelectionOnClick
                                // Pagination
                                pagination
                                paginationMode="server"
                                rowCount={totalCount}
                                paginationModel={{ page, pageSize }}
                                onPaginationModelChange={(model) => {
                                    setPage(model.page);
                                    setPageSize(model.pageSize);
                                }}
                                rowsPerPageOptions={[pageSize]}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: `1px solid ${theme.palette.divider}`
                                    },
                                    '& .MuiTablePagination-selectLabel': {
                                        display: 'none !important'
                                    },
                                    '& .MuiTablePagination-input': {
                                        display: 'none !important'
                                    },
                                    '& .MuiTablePagination-select': {
                                        display: 'none !important'
                                    }
                                }}
                            />
                        </Box>
                    </SubCard>
                </motion.div>
                <Spinner loading={loading} />
                <PVDetailsModal open={viewModalOpen} onClose={() => setViewModalOpen(false)} data={selectedRow} />
            </Box>
        </MainCard>
    );
};

export default PhysicalVerificationMonitor;
